// Pure active-view state machine for the PowerPoint add-in.
// Turns raw 'read'/'edit' view signals (from getActiveViewAsync / ActiveViewChanged /
// ResizeObserver re-sync) into a debounced decision. Mirrors Mentimeter's approach:
// a brief 'edit' blip during slide transitions must NOT drop us out of presenting,
// so 'edit' after 'read' is only committed if a re-query still reports 'edit' after a
// short debounce. A generation counter discards stale async re-query results.
//
// The re-query is two-sided: an answer of 'read' does not just cancel the pending 'edit', it
// re-enters read (commitView + onEnterRead), so a frame whose caller reacted to the blip gets
// its presenting state put back rather than left half-torn-down.
//
// No Office.js or Vuex imports — all side effects are injected.

export const READ_TO_EDIT_DEBOUNCE_MS = 200

// onEnterRead fires on EVERY committed 'read' signal (not only the first), so returning
// to a slide after navigating away re-triggers it — intentional for auto-start callers.
export const createPptActiveViewMachine = ({ getActiveView, commitView, onEnterRead }) => {
  let hasBeenRead = false
  let pendingEditTimer = null
  let generation = 0

  const cancelPendingEdit = () => {
    if (pendingEditTimer !== null) {
      clearTimeout(pendingEditTimer)
      pendingEditTimer = null
    }
  }

  const handleView = (view) => {
    // Bumped on every signal so any in-flight re-query knows it has been superseded.
    generation += 1

    if (view === 'read') {
      hasBeenRead = true
      cancelPendingEdit()
      commitView('read')
      onEnterRead()
      return
    }

    // Any non-read view is treated as 'edit'.
    if (!hasBeenRead) {
      commitView('edit')
      return
    }

    // 'edit' after having been 'read': debounce + re-query before trusting it.
    cancelPendingEdit()
    const myGeneration = generation
    pendingEditTimer = setTimeout(async () => {
      pendingEditTimer = null
      let latest
      try {
        latest = await getActiveView()
      } catch (_) {
        if (myGeneration === generation) commitView('edit')
        return
      }
      if (myGeneration !== generation) return
      if (latest === 'edit') {
        commitView('edit')
        return
      }
      // The host says we are STILL in the show, so the 'edit' was a blip — a slide transition,
      // or a click into the editing window that PowerPoint left open behind the show. Treated as
      // a fresh 'read' rather than merely dropped: onEnterRead is what restarts the slide-focus
      // poll and re-asserts this frame's slide, and between the blip and now the caller may have
      // torn both down on the strength of the 'edit' it saw. Re-firing costs nothing when it
      // did not — startTagSlideFocusPolling no-ops while a poll is running, and the auto-start
      // action no-ops while this slide is already the presenting one.
      //
      // Only for an explicit 'read'. An absent/unknown answer stays dropped: it is not evidence
      // that the show is still up, and acting on it would re-assert on a host that cannot say.
      if (latest === 'read') handleView('read')
    }, READ_TO_EDIT_DEBOUNCE_MS)
  }

  const dispose = () => {
    cancelPendingEdit()
  }

  return { handleView, dispose }
}
